import moment from 'moment'

// RD-4074: chauffeur arrival buffer — minutes after the tracked flight arrival.
// Flight times and stored pick-ups are the airport's LOCAL wall clock written as UTC (the app's
// convention), so all arithmetic is moment.utc/parseZone on the wall clock; never new Date().
export const CHAUFFEUR_ARRIVAL_BUFFERS = [0, 15, 30, 45, 60]

export const isChauffeurArrivalBuffer = v =>
  typeof v === 'number' && CHAUFFEUR_ARRIVAL_BUFFERS.includes(v)

export const chauffeurArrivalBufferLabel = v => {
  if (!isChauffeurArrivalBuffer(v)) return null
  return v === 0
    ? 'When the flight arrives'
    : `${v} minutes after flight arrival`
}

// Partner/driver banner (RD-4074): imperative copy for when to arrive at the airport.
export const chauffeurArrivalDriverTitle = v => {
  if (!isChauffeurArrivalBuffer(v)) return null
  return v === 0
    ? 'Arrive when the flight lands'
    : `Arrive ${v} min after flight landing`
}

// Stored pick-up is flight arrival + buffer; reverse that for the scheduled landing time.
export const scheduledFlightArrivalFromPickUp = (pickUpDate, buffer) => {
  if (!isChauffeurArrivalBuffer(buffer) || !pickUpDate) return null
  const pickUp = moment.utc(pickUpDate)
  if (!pickUp.isValid()) return null
  return pickUp.clone().subtract(buffer, 'minutes')
}

export const CHAUFFEUR_ARRIVAL_OPTIONS = CHAUFFEUR_ARRIVAL_BUFFERS.map(
  value => ({
    value,
    label: chauffeurArrivalBufferLabel(value),
  })
)

// Wall-clock arrival: parseZone keeps "10:00+05:00" as 10:00; the result is re-labelled UTC.
const wallClock = iso => {
  if (!iso) return null
  const m = moment.parseZone(iso)
  if (!m.isValid()) return null
  return moment.utc(m.format('YYYY-MM-DDTHH:mm:ss'))
}

export const applyBuffer = (arrivalIso, minutes) => {
  if (!isChauffeurArrivalBuffer(minutes)) return null
  const arrival = wallClock(arrivalIso)
  return arrival ? arrival.add(minutes, 'minutes') : null
}

export const inferBuffer = (pickUpMoment, arrivalIso) => {
  const arrival = wallClock(arrivalIso)
  if (
    !arrival ||
    !pickUpMoment ||
    !moment.isMoment(pickUpMoment) ||
    !pickUpMoment.isValid()
  ) {
    return null
  }
  const diff = moment.utc(pickUpMoment.format('YYYY-MM-DDTHH:mm')).diff(
    arrival
      .clone()
      .seconds(0)
      .milliseconds(0),
    'minutes'
  )
  return isChauffeurArrivalBuffer(diff) ? diff : null
}

// RD-4144 admin banner for a pick-up set by hand: "Buffer: 15 minutes · Pick-up time: 10:15".
export const manualPickUpBufferText = v => {
  if (!isChauffeurArrivalBuffer(v)) return null
  return v === 0 ? 'When the flight arrives' : `${v} minutes`
}

export const parenSuffix = v => {
  const label = chauffeurArrivalBufferLabel(v)
  return label ? ` (${label})` : ''
}

export const estimatedArrivalText = m =>
  m && m.isValid() ? `${m.format('ddd, D MMM')} · ${m.format('HH:mm')}` : ''

// Admin inline edit (RD-4074): the cached flight answers for the booking's current day only.
// When the admin moves the pick-up to another day, the same flight is estimated to land at
// the same local time that many days later (`fromDate` / `toDate` are 'DD/MM/YYYY').
export const shiftedArrivalPickUp = (arrivalIso, fromDate, toDate, minutes) => {
  const from = moment.utc(fromDate, 'DD/MM/YYYY', true)
  const to = moment.utc(toDate, 'DD/MM/YYYY', true)
  const days = from.isValid() && to.isValid() ? to.diff(from, 'days') : 0
  const pickUp = applyBuffer(arrivalIso, minutes)
  return pickUp ? pickUp.add(days, 'days') : null
}

// RD-4074: the Book step rewrites searchValues.pickUpDate/Time with arrival + buffer and keeps
// the client's own step-1 entry in manualPickUpDate/Time. Going back to Search must show (and
// re-submit) the client's own entry, not the derived time, and must not carry a stale pair
// into the next search.
export const searchValuesForSearchForm = searchValues => {
  if (!searchValues || !searchValues.manualPickUpTime) return searchValues
  const { manualPickUpDate, manualPickUpTime, ...rest } = searchValues
  return {
    ...rest,
    pickUpDate: manualPickUpDate || rest.pickUpDate,
    pickUpTime: manualPickUpTime,
  }
}
